/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * CallControlCluster.tsx — Cal\1 \2ontro\1 \2luster (services/operator-web-app).
 */

import { useCall, useCanAcceptCall, useOperatorStore } from '@/store';
import { buildCallAccept, buildCallReject, buildCallEnd } from '@/realtime/commands';
import { Button } from '@/components/ui';
import { useCallDuration, formatCallDuration } from './useCallDuration';
import { useVoiceConnection } from './useVoiceConnection';
import { useTranslation } from '@/i18n';

/**
 * E3-S3: Sticky call controls — Accept/Reject when offered, End/Mute/Hold when connected.
 * E3-S2: On Accept start voice transport (WebSocket/WebRTC); on End tear down voice.
 * Plan: 04_call_engine, 14_wireframes. Guardrails: accept disabled when paused/offline.
 */
export function CallControlCluster() {
  const t = useTranslation();
  const call = useCall();
  const canAccept = useCanAcceptCall();
  const send = useOperatorStore((s) => s.send);
  const durationSeconds = useCallDuration();
  const { startVoice, endVoice } = useVoiceConnection();
  const callMuted = useOperatorStore((s) => s.callMuted);
  const callHeld = useOperatorStore((s) => s.callHeld);
  const setCallMuted = useOperatorStore((s) => s.setCallMuted);
  const setCallHeld = useOperatorStore((s) => s.setCallHeld);
  const voiceConnectionState = useOperatorStore((s) => s.voiceConnectionState);

  if (call.phase === 'idle' || call.phase === 'ended') return null;

  const handleAccept = () => {
    if (call.callId && canAccept) {
      send(buildCallAccept(call.callId));
      startVoice({ callId: call.callId, sessionId: call.sessionId });
    }
  };

  const handleReject = () => {
    if (call.callId) send(buildCallReject(call.callId, 'declined'));
  };

  const handleEnd = () => {
    if (call.callId) {
      endVoice();
      send(buildCallEnd(call.callId, 'user_hangup'));
    }
  };

  if (call.phase === 'offered') {
    const fromLabel = call.offer?.from ?? t('call.unknown_caller');
    return (
      <>
        <div className="sr-only" role="status" aria-live="assertive" aria-atomic>
          {t('call.incoming_from', { caller: fromLabel })}
        </div>
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
          role="group"
          aria-label={t('call.incoming')}
        >
          <span className="text-sm text-amber-800 dark:text-amber-200 truncate max-w-[120px]">
            {call.offer?.from ?? '—'}
          </span>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAccept}
            disabled={!canAccept}
            aria-label={t('call.accept')}
            aria-describedby={!canAccept ? 'accept-disabled-hint' : undefined}
          >
            {t('call.accept_short')}
          </Button>
          {!canAccept && (
            <p id="accept-disabled-hint" className="sr-only">
              {t('call.accept_disabled')}
            </p>
          )}
          <Button variant="danger" size="sm" onClick={handleReject} aria-label={t('call.reject')}>
            {t('call.reject_short')}
          </Button>
        </div>
      </>
    );
  }

  if (call.phase === 'assigned' || call.phase === 'connected') {
    return (
      <div className="flex items-center gap-3" role="group" aria-label={t('call.controls')}>
        <div className="sr-only" role="status" aria-live="polite" aria-atomic>
          {callMuted ? t('call.muted') : ''}
          {callHeld ? t('call.held') : ''}
        </div>
        {call.phase === 'connected' && (
          <span
            className="text-sm font-mono tabular-nums"
            style={{ color: 'var(--text-secondary)' }}
            aria-live="polite"
          >
            {formatCallDuration(durationSeconds)}
          </span>
        )}
        <span
          className="text-xs"
          style={{ color: 'var(--text-muted)' }}
          aria-live="polite"
          title={t('call.voice_status')}
        >
          {voiceConnectionState === 'active'
            ? t('call.voice_connected')
            : voiceConnectionState === 'connecting' || voiceConnectionState === 'starting'
              ? t('call.voice_connecting')
              : voiceConnectionState === 'ended'
                ? t('call.voice_disconnected')
                : t('call.voice_unknown')}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCallMuted(!callMuted)}
          aria-label={callMuted ? t('call.unmute') : t('call.mute')}
          aria-pressed={callMuted}
        >
          {callMuted ? t('call.unmute_short') : t('call.mute_short')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCallHeld(!callHeld)}
          aria-label={callHeld ? t('call.unhold') : t('call.hold')}
          aria-pressed={callHeld}
        >
          {callHeld ? t('call.unhold_short') : t('call.hold_short')}
        </Button>
        <Button variant="danger" size="sm" onClick={handleEnd} aria-label={t('call.end')}>
          {t('call.end_short')}
        </Button>
      </div>
    );
  }

  return null;
}
